import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle, XCircle, Search, MessageSquare, RefreshCw } from "lucide-react";

const TYPE_LABELS = {
  confirmation: { label: "Confirmation RDV", color: "bg-blue-100 text-blue-700" },
  rappel: { label: "Rappel veille", color: "bg-amber-100 text-amber-700" },
  termine: { label: "Véhicule prêt", color: "bg-emerald-100 text-emerald-700" },
  avancement: { label: "Avancement", color: "bg-purple-100 text-purple-700" },
  devis: { label: "Devis", color: "bg-orange-100 text-orange-700" },
  modification: { label: "Modif. RDV", color: "bg-cyan-100 text-cyan-700" },
  personnalise: { label: "Personnalisé", color: "bg-slate-100 text-slate-700" },
  test: { label: "Test config", color: "bg-pink-100 text-pink-700" },
};

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function JournalTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [client, setClient] = useState("");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (client && client.length >= 2) params.client = client;
      const res = await api.get("/sms/logs", { params });
      setLogs(res.data || []);
    } catch (e) {
      toast.error("Erreur lors du chargement du journal");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, client]);

  useEffect(() => { fetchLogs(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="sms-journal-tab">
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs">Du</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
            </div>
            <div>
              <Label className="text-xs">Au</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
            </div>
            <div>
              <Label className="text-xs">Client / Téléphone</Label>
              <Input placeholder="Nom ou numéro..." value={client} onChange={(e) => setClient(e.target.value)} data-testid="filter-client" />
            </div>
            <Button onClick={fetchLogs} className="bg-red-600 hover:bg-red-700" data-testid="apply-filters-btn">
              <Search className="h-4 w-4 mr-2" /> Filtrer
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">{logs.length} SMS</CardTitle>
          <Button variant="outline" size="sm" onClick={fetchLogs} disabled={loading} data-testid="refresh-logs-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date / Heure</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead className="text-center">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-400 py-8">Aucun SMS trouvé</TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => {
                    const t = TYPE_LABELS[log.type] || TYPE_LABELS.personnalise;
                    return (
                      <TableRow key={log.id} data-testid="sms-log-row">
                        <TableCell className="whitespace-nowrap text-sm">{formatDate(log.created_at)}</TableCell>
                        <TableCell className="text-sm font-medium">{log.client_name || "—"}</TableCell>
                        <TableCell className="text-sm text-slate-500">{log.phone}</TableCell>
                        <TableCell><Badge className={`${t.color} border-0`}>{t.label}</Badge></TableCell>
                        <TableCell className="text-xs text-slate-500 max-w-xs truncate" title={log.message}>{log.message}</TableCell>
                        <TableCell className="text-center">
                          {log.success ? (
                            <CheckCircle className="h-5 w-5 text-emerald-500 inline" data-testid="status-success" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-400 inline" data-testid="status-failed" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function SmsCenter() {
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    api.get("/sms/credits").then((r) => setCredits(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="sms-center-view">
      <div className="flex items-center gap-3 flex-wrap">
        <MessageSquare className="h-7 w-7 text-red-600" />
        <h1 className="text-2xl font-bold text-slate-900">Journal SMS</h1>
        {credits?.success && (
          <Badge
            className={`${(credits.nb_sms ?? 0) < 50 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"} text-sm`}
            data-testid="sms-credits-badge"
          >
            Solde : {credits.balance} € · ~{credits.nb_sms} SMS restants
          </Badge>
        )}
      </div>

      <JournalTab />
    </div>
  );
}
